import {
  Badge,
  Button,
  EmptyState,
  FormField,
  KeyValue,
  List,
  ListRow,
  MoreMenu,
  NumberStepper,
  PageHeader,
  Section,
  Select,
  Skeleton,
  Text,
  useToast,
  type MenuItem,
} from '@ui';
import { Fragment, useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { BackLink } from '../../../app/BackLink';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { FormDialog } from '../../../app/FormDialog';
import { errorMessage } from '../../../lib/errors';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useSession } from '../../identity/session';
import {
  useCancelMarketDay,
  useCloseMarketDay,
  useCollectiveEntry,
  useMarketDay,
  usePrices,
  type AssortmentItem,
  type MarketDay,
  type PaymentMethod,
  type SaleListItem,
} from '../api';
import { AssortmentDialog } from '../components/AssortmentDialog';
import { useReferrerName } from '../components/Referrer';
import { SellersDialog } from '../components/SellersDialog';
import { timesText } from '../components/scheduleText';
import { QuickInvoice } from '../components/QuickInvoice';
import { SalesTable } from '../components/SalesTable';
import { formatEuro } from '../format';
import styles from './SalesPages.module.css';

const METHODS: PaymentMethod[] = ['cash', 'transfer', 'paypal', 'other'];

/** A market day with its sales and the daily closing (MKT-003 – MKT-005). */
export function MarketDayPage() {
  const { t } = useTranslation();
  const { marketDayId = '' } = useParams();
  const day = useMarketDay(marketDayId);
  if (day.error) {
    return (
      <EmptyState
        size="page"
        level={1}
        title={errorMessage(day.error)}
        actions={<BackLink to="/verkauf/markttage" label={t('sales.tabs.marketDays')} />}
      />
    );
  }
  if (!day.data) {
    return (
      <div role="status" aria-label={t('common.loading')} className={styles.page}>
        <Skeleton width="40%" height="2rem" />
        <Skeleton lines={8} />
      </div>
    );
  }
  return <MarketDayDetail day={day.data} />;
}

function MarketDayDetail({ day }: { day: MarketDay }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = useSession();
  const [open, setOpen] = useState<
    'collective' | 'close' | 'assortment' | 'sellers' | 'cancel' | null
  >(null);
  const [invoiceSale, setInvoiceSale] = useState<SaleListItem | null>(null);
  const sellerName = useReferrerName();
  const closeDay = useCloseMarketDay();
  const cancelDay = useCancelMarketDay();
  const isOpen = day.status === 'open';
  const write = can('sales.write');
  const summary = day.summary;
  const times = timesText(t, day.starts_at, day.ends_at);
  const meta: ReactNode[] = [
    formatDate(day.held_on),
    ...(times ? [times] : []),
    ...(day.place ? [day.place] : []),
    day.status === 'canceled' ? (
      <Badge key="status" tone="neutral" size="sm">
        {t('sales.market.status.canceled')}
      </Badge>
    ) : isOpen ? (
      <Badge key="status" tone="success" size="sm">
        {t('sales.market.status.open')}
      </Badge>
    ) : day.closed_at ? (
      t('sales.market.closedAt', { date: formatDateTime(day.closed_at) })
    ) : (
      t('sales.market.status.closed')
    ),
  ];
  const more: MenuItem[] = [
    {
      key: 'assortment',
      label: t('sales.assortment.edit'),
      onSelect: () => setOpen('assortment'),
    },
    {
      key: 'collective',
      label: t('sales.market.collective'),
      onSelect: () => setOpen('collective'),
    },
    { key: 'close', label: t('sales.market.close'), onSelect: () => setOpen('close') },
    ...(summary.sales === 0
      ? [{ key: 'cancel', label: t('sales.market.cancel'), onSelect: () => setOpen('cancel') }]
      : []),
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        back={<BackLink to={`/verkauf/maerkte/${day.market_id}`} label={t('sales.tabs.markets')} />}
        title={day.name}
        meta={meta.map((m, i) => (
          <Fragment key={i}>
            {i > 0 && ' · '}
            {m}
          </Fragment>
        ))}
        actions={
          write &&
          isOpen && (
            <>
              <Button onClick={() => void navigate(`/markt/${day.id}`)}>
                {t('sales.market.mode')}
              </Button>
              <MoreMenu label={t('common.moreActions')} items={more} />
            </>
          )
        }
      />
      <div className={styles.columns}>
        <Section title={t('sales.market.salesTitle')} variant="flush">
          <SalesTable
            caption={t('sales.market.salesTitle')}
            rows={day.sales}
            {...(can('invoices.write') ? { onInvoice: setInvoiceSale } : {})}
            hideCustomer
            hideDate
            empty={<EmptyState title={t('sales.market.noSales')} />}
          />
        </Section>
        <div className={styles.stack}>
          <Section title={t('sales.market.summaryTitle')}>
            <KeyValue
              items={[
                {
                  key: 'total',
                  label: t('sales.market.total'),
                  value: (
                    <Text as="span" numeric>
                      {formatEuro(summary.total_cents)}
                    </Text>
                  ),
                },
                { key: 'sales', label: t('sales.market.sales'), value: String(summary.sales) },
                {
                  key: 'jars',
                  label: t('sales.market.jars'),
                  value: String(summary.by_article.reduce((s, a) => s + a.quantity, 0)),
                },
                {
                  key: 'notes',
                  label: t('sales.form.notes'),
                  value: day.notes && <span className={styles.notes}>{day.notes}</span>,
                },
              ]}
            />
          </Section>
          {(day.assortment.length > 0 || (write && isOpen)) && (
            <Section title={t('sales.assortment.title')}>
              {day.assortment.length === 0 ? (
                <List>
                  <ListRow
                    title={t('sales.assortment.none')}
                    description={t('sales.assortment.noneHint')}
                    onClick={() => setOpen('assortment')}
                  />
                </List>
              ) : (
                <List>
                  {day.assortment.map((a) => (
                    <ListRow
                      key={a.article.id}
                      title={a.article.name}
                      description={assortmentDescription(a, isOpen, t)}
                      trailing={
                        <Text as="span" numeric>
                          {formatEuro(a.price_cents ?? a.list_price_cents ?? 0)}
                        </Text>
                      }
                    />
                  ))}
                </List>
              )}
            </Section>
          )}
          {(summary.by_seller.length > 0 || (write && isOpen)) && (
            <Section
              title={t('sales.sellers.title')}
              action={
                write &&
                isOpen && (
                  <Button variant="secondary" size="sm" onClick={() => setOpen('sellers')}>
                    {t('sales.sellers.edit')}
                  </Button>
                )
              }
            >
              {summary.by_seller.length === 0 ? (
                <List>
                  <ListRow title={t('sales.sellers.none')} />
                </List>
              ) : (
                <List>
                  {summary.by_seller.map((s) => (
                    <ListRow
                      key={s.seller.id}
                      title={sellerName(s.seller)}
                      description={t('sales.market.salesCount', { count: s.sales })}
                      trailing={
                        <Text as="span" numeric>
                          {formatEuro(s.total_cents)}
                        </Text>
                      }
                    />
                  ))}
                </List>
              )}
            </Section>
          )}
          {summary.by_article.length > 0 && (
            <Section title={t('sales.market.byArticle')}>
              <List>
                {summary.by_article.map((a) => (
                  <ListRow
                    key={a.article.id}
                    title={a.article.name}
                    description={t('sales.market.jarCount', { count: a.quantity })}
                    trailing={
                      <Text as="span" numeric>
                        {formatEuro(a.total_cents)}
                      </Text>
                    }
                  />
                ))}
              </List>
            </Section>
          )}
          {summary.by_method.length > 0 && (
            <Section title={t('sales.market.byMethod')}>
              <List>
                {summary.by_method.map((m) => (
                  <ListRow
                    key={m.method}
                    title={t(`sales.method.${m.method}`)}
                    description={t('sales.market.salesCount', { count: m.sales })}
                    trailing={
                      <Text as="span" numeric>
                        {formatEuro(m.total_cents)}
                      </Text>
                    }
                  />
                ))}
              </List>
            </Section>
          )}
        </div>
      </div>

      {invoiceSale && <QuickInvoice sale={invoiceSale} onClose={() => setInvoiceSale(null)} />}
      {open === 'collective' && <CollectiveDialog day={day} onClose={() => setOpen(null)} />}
      {open === 'assortment' && <AssortmentDialog day={day} onClose={() => setOpen(null)} />}
      {open === 'sellers' && <SellersDialog day={day} onClose={() => setOpen(null)} />}
      <ConfirmDialog
        open={open === 'cancel'}
        onClose={() => {
          cancelDay.reset();
          setOpen(null);
        }}
        tone="default"
        title={t('sales.market.cancelTitle', { name: day.name })}
        description={t('sales.market.cancelText')}
        confirmLabel={t('sales.market.cancel')}
        pending={cancelDay.isPending}
        error={cancelDay.error ? errorMessage(cancelDay.error) : undefined}
        onConfirm={() =>
          cancelDay.mutate(day.id, {
            onSuccess: () => {
              toast.show(t('sales.market.canceled'));
              setOpen(null);
            },
          })
        }
      />
      {/* Closing is irreversible (MKT-004), hence the confirmation. */}
      <ConfirmDialog
        open={open === 'close'}
        onClose={() => {
          closeDay.reset();
          setOpen(null);
        }}
        tone="default"
        title={t('sales.market.closeTitle', { name: day.name })}
        description={t('sales.market.closeText', {
          count: summary.sales,
          total: formatEuro(summary.total_cents),
        })}
        confirmLabel={t('sales.market.close')}
        pending={closeDay.isPending}
        error={closeDay.error ? errorMessage(closeDay.error) : undefined}
        onConfirm={() =>
          closeDay.mutate(day.id, {
            onSuccess: () => {
              toast.show(t('sales.market.closed'));
              setOpen(null);
            },
          })
        }
      />
    </div>
  );
}

/** End-of-day count: taken − returned = sold (MKT-005). */
function CollectiveDialog({ day, onClose }: { day: MarketDay; onClose: () => void }) {
  const dayId = day.id;
  const { t } = useTranslation();
  const toast = useToast();
  const prices = usePrices();
  const entry = useCollectiveEntry();
  // Taken is pre-filled from the assortment (MKT-008).
  const [counts, setCounts] = useState<Record<string, { taken: number; returned: number }>>(() =>
    Object.fromEntries(
      day.assortment
        .filter((a) => a.quantity != null)
        .map((a) => [a.article.id, { taken: a.quantity ?? 0, returned: 0 }]),
    ),
  );
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const inAssortment = day.assortment.length > 0;
  const articles = (prices.data ?? [])
    .filter((a) => !a.archived && a.current_cents != null)
    .filter((a) => !inAssortment || day.assortment.some((i) => i.article.id === a.article.id))
    .map((a) => ({
      ...a,
      current_cents:
        day.assortment.find((i) => i.article.id === a.article.id)?.price_cents ?? a.current_cents,
    }));
  const get = (id: string) => counts[id] ?? { taken: 0, returned: 0 };
  const set = (id: string, patch: Partial<{ taken: number; returned: number }>) =>
    setCounts((c) => ({ ...c, [id]: { ...get(id), ...patch } }));
  const total = articles.reduce((s, a) => {
    const c = get(a.article.id);
    return s + Math.max(0, c.taken - c.returned) * (a.current_cents ?? 0);
  }, 0);

  return (
    <FormDialog
      open
      onClose={onClose}
      drawer
      title={t('sales.market.collectiveTitle')}
      submitLabel={t('sales.market.collectiveSubmit', { total: formatEuro(total) })}
      pending={entry.isPending}
      error={entry.error ? errorMessage(entry.error) : undefined}
      onSubmit={() =>
        entry.mutate(
          {
            day: dayId,
            body: {
              method,
              entries: articles
                .map((a) => ({ article_id: a.article.id, ...get(a.article.id) }))
                .filter((e) => e.taken > 0),
            },
          },
          {
            onSuccess: () => {
              toast.show(t('sales.market.collectiveDone'));
              onClose();
            },
          },
        )
      }
    >
      {articles.length === 0 && <Text tone="muted">{t('sales.market.noPrices')}</Text>}
      {articles.map((a) => {
        const c = get(a.article.id);
        return (
          <fieldset key={a.article.id} className={styles.collective}>
            <legend>
              {a.article.name} · {formatEuro(a.current_cents ?? 0)}
            </legend>
            <FormField label={t('sales.market.taken')}>
              {(p) => (
                <NumberStepper
                  {...p}
                  value={c.taken}
                  min={0}
                  onChange={(v) => set(a.article.id, { taken: v ?? 0 })}
                  labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
                />
              )}
            </FormField>
            <FormField label={t('sales.market.returned')}>
              {(p) => (
                <NumberStepper
                  {...p}
                  value={c.returned}
                  min={0}
                  onChange={(v) => set(a.article.id, { returned: v ?? 0 })}
                  labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
                />
              )}
            </FormField>
            <Text size="sm" tone={c.returned > c.taken ? 'danger' : 'muted'}>
              {t('sales.market.sold', { count: Math.max(0, c.taken - c.returned) })}
            </Text>
          </fieldset>
        );
      })}
      <FormField label={t('sales.form.method')}>
        {(p) => (
          <Select
            {...p}
            value={method}
            onChange={(e) => setMethod(e.target.value as PaymentMethod)}
            options={METHODS.map((m) => ({ value: m, label: t(`sales.method.${m}`) }))}
          />
        )}
      </FormField>
    </FormDialog>
  );
}

/** "mitgenommen 12 · verkauft 9 · zurück 3 · Listenpreis 7,00 €" (MKT-008). */
function assortmentDescription(a: AssortmentItem, isOpen: boolean, t: TFunction): string {
  const parts: string[] = [];
  if (a.quantity != null) parts.push(t('sales.assortment.taken', { count: a.quantity }));
  if (!isOpen || a.sold > 0) parts.push(t('sales.assortment.sold', { count: a.sold }));
  if (!isOpen && a.quantity != null) {
    parts.push(t('sales.assortment.returned', { count: Math.max(0, a.quantity - a.sold) }));
  }
  if (a.price_cents != null && a.list_price_cents != null && a.price_cents !== a.list_price_cents) {
    parts.push(t('sales.assortment.listPrice', { price: formatEuro(a.list_price_cents) }));
  }
  return parts.join(' · ');
}
